import React, { useEffect, useRef } from 'react'
import { CalendarStep } from '../types/dimmingProfile.types'
import { DimmingProfileUiStrings } from '../i18n/dimmingProfileI18n'
import {
  useCalendarStepsPresenter,
  ZEN_MAX,
  ZEN_MIN,
} from '../CalendarStepsPresenter'

interface CalendarStepsCurveProps {
  steps: CalendarStep[]
  onChange: (next: CalendarStep[]) => void
  svgRef?: React.RefObject<SVGSVGElement>
  strings: DimmingProfileUiStrings
  title: string
  color?: string
  /**
   * Optional directional cue. When set, a small arrow + label is drawn near the
   * top of the plot: 'sunrise' points toward LOWER zenith (left, sun climbing),
   * 'sunset' points toward HIGHER zenith (right, sun descending).
   */
  phase?: 'sunrise' | 'sunset'
  // Lecture seule : ni ajout, ni glisser, ni suppression au double-clic.
  readOnly?: boolean
}

// Light theme, mirroring ProfileCurve.tsx so the editors read as a set.
const COLOR_AXIS = '#9ca3af'
const COLOR_GRID = '#eef2f7'
const COLOR_GRID_MAJOR = '#dde3ec'
const COLOR_CURVE = '#2563eb'
const COLOR_HANDLE_SELECTED = '#1d4ed8'

// X grid ticks on the zenith axis (degrees); Y ticks on the level axis (%).
const ZENITH_TICKS = [80, 85, 90, 95, 100, 105, 110, 115, 120]
const LEVEL_TICKS = [0, 25, 50, 75, 100]

// ── Sun-position reference cues ───────────────────────────────────────────────
// The zenith angle θ is literally the sun's position: θ < 90° sun is above the
// horizon (day), θ = 90° sun is exactly at the horizon, θ > 90° sun is below it
// (twilight then night). These bands/glyphs make that legible without the reader
// knowing what "zenith angle" means.
const ZEN_HORIZON = 90 // sun exactly at the horizon
const ZEN_NIGHT = 108 // past astronomical twilight = full night
const COLOR_BAND_DAY = '#fffbeb' // warm light tint (daylight)
const COLOR_BAND_TWILIGHT = '#eef2ff' // soft blue (twilight)
const COLOR_BAND_NIGHT = '#e2e8f0' // deeper slate (night)
const COLOR_HORIZON = '#f59e0b' // amber horizon marker
const COLOR_ZONE_LABEL = '#6b7280'

/**
 * A small sun glyph: a circle plus short straight rays. Pure presentation, no
 * emoji. `hollow` draws an outline-only sun (used for the below-horizon sun).
 */
const SunGlyph: React.FC<{
  cx: number
  cy: number
  r: number
  color: string
  hollow?: boolean
}> = ({ cx, cy, r, color, hollow = false }) => {
  const rays = [0, 45, 90, 135, 180, 225, 270, 315]
  const rayInner = r + 1.5
  const rayOuter = r + 4
  return (
    <g pointerEvents="none">
      {rays.map((deg) => {
        const rad = (deg * Math.PI) / 180
        const dx = Math.cos(rad)
        const dy = Math.sin(rad)
        return (
          <line
            key={`ray-${deg}`}
            x1={cx + dx * rayInner}
            y1={cy + dy * rayInner}
            x2={cx + dx * rayOuter}
            y2={cy + dy * rayOuter}
            stroke={color}
            strokeWidth={1}
            strokeDasharray={hollow ? '2 1.5' : undefined}
            opacity={hollow ? 0.7 : 1}
          />
        )
      })}
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill={hollow ? 'none' : color}
        stroke={color}
        strokeWidth={1}
        strokeDasharray={hollow ? '2 1.5' : undefined}
        opacity={hollow ? 0.7 : 1}
      />
    </g>
  )
}

/**
 * Direct zenith-axis step editor for one calendar steps array (sunrise OR
 * sunset). Mirrors ProfileCurve.tsx exactly (SVG, step-curve path, draggable
 * circle handles, selection, tooltip, and CRUCIALLY the window-level
 * pointermove/pointerup listeners that keep dragging smooth anywhere on screen),
 * but on a zenith axis instead of a time axis. Self-contained: it creates its
 * own svgRef and drives the CalendarStepsPresenter.
 */
const CalendarStepsCurve: React.FC<CalendarStepsCurveProps> = ({
  steps: stepsProp,
  onChange,
  svgRef: svgRefProp,
  strings,
  title,
  color = COLOR_CURVE,
  phase,
  readOnly = false,
}) => {
  const localRef = useRef<SVGSVGElement>(null)
  const svgRef = svgRefProp ?? localRef
  // SUNRISE mirrors the zenith axis so TIME flows left→right on both charts:
  // at dawn the sun moves toward LOWER zenith angles (night 120° → day 80°).
  const reversed = phase === 'sunrise'
  const { state, actions } = useCalendarStepsPresenter({
    steps: stepsProp,
    onChange,
    svgRef,
    reversed,
  })
  const { geometry: g, steps, selectedStepIndex, draggingStepIndex } = state

  // Drag is driven by window listeners so it keeps tracking outside the SVG.
  useEffect(() => {
    if (draggingStepIndex === null) return
    const handleMove = (e: PointerEvent) => actions.dragTo(e.clientX, e.clientY)
    const handleUp = () => actions.endDrag()
    window.addEventListener('pointermove', handleMove)
    window.addEventListener('pointerup', handleUp)
    return () => {
      window.removeEventListener('pointermove', handleMove)
      window.removeEventListener('pointerup', handleUp)
    }
  }, [draggingStepIndex, actions])

  const baselineY = actions.levelToY(0)

  // ── Sun-position reference geometry ─────────────────────────────────────────
  // Bands span the full plot height; the sky strip lives just under the top
  // margin. All clamped to the plot edges so cues never spill outside the frame.
  const plotTop = g.marginTop
  const plotBottom = g.marginTop + g.plotHeight
  const plotRight = g.marginLeft + g.plotWidth
  const xDayStart = actions.zenToX(ZEN_MIN)
  const xHorizon = actions.zenToX(ZEN_HORIZON)
  const xNight = actions.zenToX(ZEN_NIGHT)
  const xDayEnd = actions.zenToX(ZEN_MAX)
  // Sky strip: a short band at the top of the plot whose baseline is the horizon
  // line; suns sit above (day) or below (night) it to show the sun dropping.
  const skyTop = plotTop + 4
  const skyHeight = Math.min(34, g.plotHeight * 0.32)
  const skyHorizonY = skyTop + skyHeight * 0.6
  const xSunDay = actions.zenToX(82)
  const xSunNight = actions.zenToX(114)

  // Build the step-curve path: a staircase on the zenith axis matching
  // levelForZenith (utils/solar.ts) — the model the lamp follows: BELOW the
  // first threshold (full daylight, θ < first step) the level is 0%, each
  // step's level applies from its zenith upward, and the last level holds to
  // ZEN_MAX. The previous version wrongly extended the FIRST step's level all
  // the way down to ZEN_MIN, which made the day side unreadable.
  const buildStepPath = (): string => {
    if (steps.length === 0) return ''
    const pts: string[] = []
    let prevY = actions.levelToY(0) // daylight default before any threshold
    pts.push(`M ${actions.zenToX(ZEN_MIN)} ${prevY}`)
    for (const s of steps) {
      const x = actions.zenToX(s.zenith_angle_deg)
      const y = actions.levelToY(s.dimming_level_percent)
      pts.push(`L ${x} ${prevY}`)
      pts.push(`L ${x} ${y}`)
      prevY = y
    }
    pts.push(`L ${actions.zenToX(ZEN_MAX)} ${prevY}`)
    return pts.join(' ')
  }

  return (
    <div style={{ width: '100%', maxWidth: 640 }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#111827' }}>
        {title}
      </div>
      <svg
        ref={svgRef}
        data-testid={`calendar-steps-curve-${phase ?? 'steps'}`}
        viewBox={`0 0 ${g.width} ${g.height}`}
        style={{
          width: '100%',
          height: 'auto',
          touchAction: 'none',
          userSelect: 'none',
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 6,
        }}
        onClick={(e) => {
          // Only treat clicks on the background (not on a handle) as "add".
          if (readOnly) return
          if ((e.target as Element).getAttribute('data-handle') === '1') return
          actions.handlePlotClick(e.clientX, e.clientY)
        }}
      >
        {/* ── Sun-position reference cues (drawn FIRST, behind everything) ── */}

        {/* 1. Day / twilight / night background bands (min/abs: the sunrise
            chart mirrors the axis, so band edges may arrive right-to-left). */}
        <g pointerEvents="none">
          <rect
            x={Math.min(xDayStart, xHorizon)}
            y={plotTop}
            width={Math.abs(xHorizon - xDayStart)}
            height={g.plotHeight}
            fill={COLOR_BAND_DAY}
            opacity={0.6}
          />
          <rect
            x={Math.min(xHorizon, xNight)}
            y={plotTop}
            width={Math.abs(xNight - xHorizon)}
            height={g.plotHeight}
            fill={COLOR_BAND_TWILIGHT}
            opacity={0.7}
          />
          <rect
            x={Math.min(xNight, xDayEnd)}
            y={plotTop}
            width={Math.abs(xDayEnd - xNight)}
            height={g.plotHeight}
            fill={COLOR_BAND_NIGHT}
            opacity={0.7}
          />
        </g>

        {/* 2. Sky strip: horizon baseline + 3 suns showing the sun dropping */}
        <g pointerEvents="none">
          <line
            x1={g.marginLeft}
            y1={skyHorizonY}
            x2={plotRight}
            y2={skyHorizonY}
            stroke={COLOR_HORIZON}
            strokeWidth={1}
            opacity={0.35}
          />
          {/* Day: sun high above the horizon line */}
          <SunGlyph cx={xSunDay} cy={skyTop + 4} r={3.5} color={COLOR_HORIZON} />
          {/* Horizon: sun sitting on the line */}
          <SunGlyph
            cx={xHorizon}
            cy={skyHorizonY}
            r={3.5}
            color={COLOR_HORIZON}
          />
          {/* Night: hollow sun below the horizon line */}
          <SunGlyph
            cx={xSunNight}
            cy={skyHorizonY + skyHeight * 0.32}
            r={3.5}
            color="#64748b"
            hollow
          />
        </g>

        {/* 3. Zone labels centered over each band */}
        <g pointerEvents="none">
          <text
            x={(xDayStart + xHorizon) / 2}
            y={skyTop + skyHeight + 12}
            textAnchor="middle"
            fontSize={10}
            fill={COLOR_ZONE_LABEL}
          >
            {strings.zenithDayLabel}
          </text>
          <text
            x={(xHorizon + xNight) / 2}
            y={skyTop + skyHeight + 12}
            textAnchor="middle"
            fontSize={10}
            fill={COLOR_ZONE_LABEL}
          >
            {strings.zenithTwilightLabel}
          </text>
          <text
            x={(xNight + xDayEnd) / 2}
            y={skyTop + skyHeight + 12}
            textAnchor="middle"
            fontSize={10}
            fill={COLOR_ZONE_LABEL}
          >
            {strings.zenithNightLabel}
          </text>
        </g>

        {/* 4. Horizon marker at 90° (bolder vertical line + label) */}
        <g pointerEvents="none">
          <line
            x1={xHorizon}
            y1={plotTop}
            x2={xHorizon}
            y2={plotBottom}
            stroke={COLOR_HORIZON}
            strokeWidth={1.5}
          />
          <text
            x={xHorizon + 4}
            y={plotTop + 10}
            textAnchor="start"
            fontSize={10}
            fontWeight={600}
            fill={COLOR_HORIZON}
          >
            {strings.zenithHorizonLabel}
          </text>
        </g>

        {/* 6. Optional directional cue (phase = sunrise / sunset). With the
            sunrise axis mirrored, TIME flows left→right on BOTH charts, so the
            arrow always points right — it reads as "the day progresses". */}
        {phase && (
          <g pointerEvents="none">
            <line
              x1={xHorizon + 6}
              y1={plotBottom - 10}
              x2={xHorizon + 46}
              y2={plotBottom - 10}
              stroke={COLOR_ZONE_LABEL}
              strokeWidth={1}
            />
            <path
              d={`M ${xHorizon + 46} ${plotBottom - 10} l -5 -3 l 0 6 z`}
              fill={COLOR_ZONE_LABEL}
            />
            <text
              x={xHorizon + 8}
              y={plotBottom - 14}
              textAnchor="start"
              fontSize={10}
              fill={COLOR_ZONE_LABEL}
            >
              {phase === 'sunrise' ? strings.sunRisingHint : strings.sunSettingHint}
            </text>
          </g>
        )}

        {/* Plot border */}
        <rect
          x={g.marginLeft}
          y={g.marginTop}
          width={g.plotWidth}
          height={g.plotHeight}
          fill="none"
          stroke={COLOR_AXIS}
          strokeWidth={1}
        />

        {/* Horizontal level grid + labels */}
        {LEVEL_TICKS.map((lvl) => {
          const y = actions.levelToY(lvl)
          return (
            <g key={`lvl-${lvl}`}>
              <line
                x1={g.marginLeft}
                y1={y}
                x2={g.marginLeft + g.plotWidth}
                y2={y}
                stroke={lvl === 0 ? COLOR_GRID_MAJOR : COLOR_GRID}
                strokeWidth={1}
              />
              <text
                x={g.marginLeft - 8}
                y={y + 4}
                textAnchor="end"
                fontSize={11}
                fill="#6b7280"
              >
                {lvl}
              </text>
            </g>
          )
        })}

        {/* Vertical zenith grid + labels */}
        {ZENITH_TICKS.map((zen) => {
          const x = actions.zenToX(zen)
          return (
            <g key={`zen-${zen}`}>
              <line
                x1={x}
                y1={g.marginTop}
                x2={x}
                y2={g.marginTop + g.plotHeight}
                stroke={COLOR_GRID_MAJOR}
                strokeWidth={1}
              />
              <text
                x={x}
                y={g.marginTop + g.plotHeight + 16}
                textAnchor="middle"
                fontSize={11}
                fill="#6b7280"
              >
                {zen}°
              </text>
            </g>
          )
        })}

        {/* X axis caption */}
        <text
          x={g.marginLeft + g.plotWidth / 2}
          y={g.height - 2}
          textAnchor="middle"
          fontSize={12}
          fill="#374151"
        >
          {strings.zenithAxis}
        </text>

        {/* Step curve */}
        {steps.length > 0 && (
          <path
            d={buildStepPath()}
            fill="none"
            stroke={color}
            strokeWidth={2}
          />
        )}

        {/* Step handles */}
        {steps.map((s, i) => {
          const x = actions.zenToX(s.zenith_angle_deg)
          const y = actions.levelToY(s.dimming_level_percent)
          const isSelected = i === selectedStepIndex
          return (
            <g key={`step-${i}`}>
              {/* Vertical guide to baseline for readability */}
              <line
                x1={x}
                y1={y}
                x2={x}
                y2={baselineY}
                stroke={color}
                strokeWidth={1}
                opacity={0.4}
              />
              <circle
                data-handle="1"
                cx={x}
                cy={y}
                r={isSelected ? 7 : 5.5}
                fill={isSelected ? COLOR_HANDLE_SELECTED : color}
                stroke="#fff"
                strokeWidth={2}
                style={{ cursor: readOnly ? 'pointer' : 'grab' }}
                onPointerDown={(e) => {
                  e.stopPropagation()
                  if (!readOnly) actions.beginDrag(i)
                }}
                onClick={(e) => {
                  e.stopPropagation()
                  actions.selectStep(i)
                }}
                onDoubleClick={(e) => {
                  e.stopPropagation()
                  if (!readOnly) actions.removeStep(i)
                }}
              />
              {/* Tooltip-ish label for the selected/dragged step */}
              {(isSelected || i === draggingStepIndex) && (
                <text
                  data-handle="1"
                  x={x}
                  y={y - 12}
                  textAnchor="middle"
                  fontSize={11}
                  fontWeight={600}
                  fill="#111827"
                >
                  {s.zenith_angle_deg}° · {s.dimming_level_percent}%
                </text>
              )}
            </g>
          )
        })}

        {/* Y axis caption (rotated) */}
        <text
          x={12}
          y={g.marginTop + g.plotHeight / 2}
          textAnchor="middle"
          fontSize={12}
          fill="#374151"
          transform={`rotate(-90 12 ${g.marginTop + g.plotHeight / 2})`}
        >
          {strings.levelAxis}
        </text>
      </svg>
      {!readOnly && (
        <p style={{ fontSize: 12, color: '#6b7280', margin: '6px 2px 0' }}>
          {strings.calendarStepsHint}
        </p>
      )}
      <p style={{ fontSize: 12, color: '#9ca3af', margin: '4px 2px 0' }}>
        {strings.zenithSunHint}
      </p>
    </div>
  )
}

export default CalendarStepsCurve
